import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  OutlinedInput,
  Checkbox,
  ListItemText,
  CircularProgress,
  SelectChangeEvent,
  Button
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  BidFormData,
  BidFormErrors,
  TypeOption,
  CategoryOption,
  DepartmentOption,
  StatusOption
} from './index';
import CreateTypeModal from './components/CreateTypeModal';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import CreateCategoryModal from './components/CreateCategoryModal';
import CreateDepartmentModal from './components/CreateDepartmentModal';
import { DatePicker } from '@mui/x-date-pickers';
import { addCommaWithTwoDecimals } from 'src/services/common';

interface BidDetailsFormProps {
  formData: BidFormData;
  setFormData: React.Dispatch<React.SetStateAction<BidFormData>>;
  errors: BidFormErrors;
  setErrors: React.Dispatch<React.SetStateAction<BidFormErrors>>;
  types: TypeOption[];
  categories: CategoryOption[];
  departments: DepartmentOption[];
  statuses: StatusOption[];
  isGeneratingContractNo: boolean;
  handleBidTypeChange: (selectedType: string, typeId?: number) => Promise<void>;
  fetchDropdownData: any;
}

const BidDetailsForm: React.FC<BidDetailsFormProps> = ({
  formData,
  setFormData,
  errors,
  types,
  categories,
  departments,
  statuses,
  isGeneratingContractNo,
  handleBidTypeChange,
  setErrors,
  fetchDropdownData
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isDepartmentModalOpen, setIsDepartmentModalOpen] = useState(false);
  const [openCategorySelect, setOpenCategorySelect] = useState(false);
  const [contacts, setContacts] = useState<
    { id: string; name: string; userId?: number; displayName: string }[]
  >([]);
  const [contactError, setContactError] = useState<string | null>(null);
  const [loadingContacts, setLoadingContacts] = useState<boolean>(false);
  const fetchedVendorsRef = useRef<Set<string>>(new Set());
  const [loadingVendors, setLoadingVendors] = useState<boolean>(false);
  const [vendors, setVendors] = useState<{ id: string; name: string }[]>([]);
  const [vendorError, setVendorError] = useState<string | null>(null);

  // Get current date/time for minimum date validation
  const currentDateTime = new Date();

  // Get today's date without time
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Get tomorrow's date for Go Live Date validation
  const tomorrowDateTime = new Date();
  tomorrowDateTime.setDate(currentDateTime.getDate() + 1);
  tomorrowDateTime.setHours(0, 0, 0, 0); // Set to start of tomorrow

  // Set default status to "Draft" when component mounts
  useEffect(() => {
    // Auto-select "Open"

    const openStatus = statuses.find((s) => s.Status === 'Open');
    if (openStatus && !formData?.status) {
      setFormData({
        ...formData,
        status: openStatus.Status
      });
    }
  }, []);

  const handleCreateType = async (type: string) => {
    try {
      const response = await httpRequest(
        'POST',
        `bid_portal/type/insert`,
        {
          Type: type
        },
        {}
      );

      if (response.status === 'success') {
        fetchDropdownData();
        setIsModalOpen(false);
        if (errors.bidType) {
          setErrors((prev) => ({ ...prev, bidType: '' }));
        }
        handleBidTypeChange(response?.data?.Type, response?.data?.TypeID);
        toast.success(response?.message || 'Bid type created successfully');
      }
    } catch (err: any) {
      console.error('Error:', err);
      toast.error(err.message);
    }
  };

  const handleCreateCategory = async (category: string) => {
    try {
      const response = await httpRequest(
        'POST',
        '/bid_portal/category/insert',
        {
          CategoryName: category
        },
        {}
      );

      if (response.status === 'success') {
        fetchDropdownData();
        setIsCategoryModalOpen(false);

        if (errors.categories) {
          setErrors((prev) => ({ ...prev, categories: '' }));
        }

        const newCategories =
          typeof category === 'string' ? category?.split(',') : category;
        setFormData({
          ...formData,
          categories: newCategories
        });
        toast.success(response?.message || 'Category created successfully');
      }
    } catch (err: any) {
      console.error('Error:', err);
      toast.error(err.message);
    }
  };

  const handleCreateDepartment = async (newDepartment: string) => {
    try {
      const response = await httpRequest(
        'POST',
        '/bid_portal/department/insert',
        {
          DepartmentName: newDepartment
        },
        {}
      );

      if (response.status === 'success') {
        fetchDropdownData();
        setIsDepartmentModalOpen(false);
        if (errors.department) {
          setErrors((prev) => ({ ...prev, department: '' }));
        }
        setFormData({
          ...formData,
          department: newDepartment
        });
        toast.success(response?.message || 'Department created successfully');
      }
    } catch (err: any) {
      console.error('Error:', err);
      toast.error(err.message);
    }
  };

  // Validation functions for date dependencies
  // const isGoLiveDateValid = (date: Date | null): boolean => {
  //   if (!date) return true; // Allow empty dates for optional fields
  //   return date >= tomorrowDateTime;
  // };

  // const isBidDeadlineValid = (date: Date | null): boolean => {
  //   if (!date) return true;
  //   if (!formData.goLiveDate) return date >= tomorrowDateTime;
  //   return date > formData.goLiveDate;
  // };

  // const isAwardDateValid = (date: Date | null): boolean => {
  //   if (!date) return true;
  //   if (!formData.bidDeadline) return date >= tomorrowDateTime;
  //   return date > formData.bidDeadline;
  // };

  // const getGoLiveDateError = (): string => {
  //   if (!formData.goLiveDate) return '';
  //   if (!isGoLiveDateValid(formData.goLiveDate)) {
  //     return 'Go Live Date must be greater than today';
  //   }
  //   return '';
  // };

  // const getBidDeadlineError = (): string => {
  //   if (!formData.bidDeadline) return '';
  //   if (formData.goLiveDate && formData.bidDeadline <= formData.goLiveDate) {
  //     return 'Bid Deadline must be greater than Go Live Date';
  //   }
  //   if (!isBidDeadlineValid(formData.bidDeadline)) {
  //     return 'Bid Deadline must be greater than today';
  //   }
  //   return '';
  // };

  // const getAwardDateError = (): string => {
  //   if (!formData.awardDate) return '';
  //   if (formData.bidDeadline && formData.awardDate <= formData.bidDeadline) {
  //     return 'Award Date must be greater than Bid Deadline';
  //   }
  //   if (!isAwardDateValid(formData.awardDate)) {
  //     return 'Award Date must be greater than today';
  //   }
  //   return '';
  // };

  const fetchVendors = useCallback(async () => {
    setLoadingVendors(true);
    setVendorError(null);
    try {
      const response = await httpRequest(
        'GET',
        '/auth/vendors_information',
        {},
        {}
      );
      if (response.status === 'success' && response.data?.BusinessDetails) {
        const vendorsList = response.data.BusinessDetails.map(
          (vendor: { Business: string }, index: number) => ({
            id: `vendor-${index}`,
            name: vendor.Business
          })
        );
        setVendors(vendorsList);
      } else {
        console.error('Unexpected vendor response format:', response);
        setVendorError(
          response && response?.message
            ? response.message
            : 'Failed to load vendors. Unexpected response format.'
        );
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setVendorError('Failed to load vendors. Please try again.');
    } finally {
      setLoadingVendors(false);
    }
  }, []);

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchContacts = useCallback(
    async (vendorName: string) => {
      if (!vendorName) return;
      if (fetchedVendorsRef.current.has(vendorName)) return;
      if (loadingContacts) return;

      setLoadingContacts(true);
      setContactError(null);
      setContacts([]);
      try {
        fetchedVendorsRef.current.add(vendorName);
        const url = `/auth/vendors_information?Business=${encodeURIComponent(
          vendorName
        )}`;
        const response = await httpRequest('GET', url, {}, {});
        if (response.status === 'success') {
          if (
            response.data?.VendorDetails &&
            Array.isArray(response.data.VendorDetails)
          ) {
            const contactsList = response.data.VendorDetails.map(
              (contact: any) => ({
                id: `contact-${contact.UserID || contact.FullNameWithEmail}`,
                name:
                  contact.FullNameWithEmail ||
                  `${contact.FirstName} ${contact.LastName}`,
                userId: contact.UserID,
                displayName:
                  contact.FullNameWithEmail ||
                  `${contact.FirstName} ${contact.LastName}`
              })
            );
            setContacts(contactsList);
            if (contactsList.length > 0) {
              const firstContact = contactsList[0];
              setFormData((prevData) => ({
                ...prevData,
                awardedToContact: firstContact.name,
                awardedToContactId: firstContact.userId || 0
              }));
            }
          } else {
            setContacts([]);
          }
        } else {
          setContactError('Failed to load contacts. Please try again.');
        }
      } catch (error) {
        console.error('Error fetching contacts:', error);
        setContactError('Failed to load contacts. Please try again.');
        fetchedVendorsRef.current.delete(vendorName);
      } finally {
        setLoadingContacts(false);
      }
    },
    [
      // bidId,

      loadingContacts,
      setFormData
    ]
  );

  const handleVendorChange = (e: SelectChangeEvent) => {
    const selectedVendor = e.target.value;
    const currentVendor = formData.awardedToVendor;
    if (selectedVendor !== currentVendor) {
      setFormData({
        ...formData,
        awardedToVendor: selectedVendor,
        awardedToContact: ''
      });
      if (errors.awardedToVendor && selectedVendor)
        setErrors((prev) => ({ ...prev, awardedToVendor: '' }));
      if (errors.awardedToContact)
        setErrors((prev) => ({ ...prev, awardedToContact: '' }));
      if (selectedVendor && !loadingContacts) fetchContacts(selectedVendor);
    }
  };

  const handleContactChange = (e: SelectChangeEvent) => {
    const selectedContactName = e.target.value;
    const selectedContact = contacts.find(
      (contact) => contact.name === selectedContactName
    );
    setFormData({
      ...formData,
      awardedToContact: selectedContactName,
      awardedToContactId:
        selectedContact?.userId || formData.awardedToContactId || 0
    });
    if (errors.awardedToContact && selectedContactName) {
      setErrors((prev) => ({ ...prev, awardedToContact: '' }));
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!errors.bidType} required>
          <InputLabel>Type</InputLabel>
          <Select
            value={formData.bidType}
            onChange={(e: SelectChangeEvent) => {
              handleBidTypeChange(e.target.value);
              if (errors.bidType) {
                setErrors((prev) => ({ ...prev, bidType: '' }));
              }
            }}
            label="Type"
            required
          >
            {types.map((type) => (
              <MenuItem key={type.TypeID} value={type.Type}>
                {type.Type}
              </MenuItem>
            ))}
          </Select>
          {errors.bidType && <FormHelperText>{errors.bidType}</FormHelperText>}

          {/* <Typography
            color="primary"
            sx={{
              cursor: 'pointer',
              textAlign: 'right',
              marginTop: '1px',
              fontSize: '12px',
              textDecoration: 'underline',
              fontWeight: '700'
            }}
            onClick={() => setIsModalOpen(true)}
          >
            +Create New
          </Typography> */}
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Contract No."
          value={formData.contractNo}
          onChange={(e) => {
            setFormData({ ...formData, contractNo: e.target.value });
            if (errors.contractNo && e.target.value.trim() !== '') {
              setErrors((prev) => ({ ...prev, contractNo: '' }));
            }
          }}
          InputProps={{
            endAdornment: isGeneratingContractNo ? (
              <CircularProgress size={20} />
            ) : null,
            readOnly: isGeneratingContractNo
          }}
          error={!!errors.contractNo}
          helperText={errors.contractNo}
          required
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Title"
          value={formData.title}
          onChange={(e) => {
            setFormData({ ...formData, title: e.target.value });
            if (errors.title && e.target.value.trim() !== '') {
              setErrors((prev) => ({ ...prev, title: '' }));
            }
          }}
          error={!!errors.title}
          helperText={errors.title}
          required
        />
      </Grid>

      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'end',
          flexDirection: 'column'
        }}
      >
        <FormControl fullWidth error={!!errors.categories} required>
          <InputLabel>Categories</InputLabel>
          <Select
            multiple
            open={openCategorySelect}
            onOpen={() => setOpenCategorySelect(true)}
            onClose={() => setOpenCategorySelect(false)}
            value={formData.categories}
            onChange={(e) => {
              const newCategories =
                typeof e.target.value === 'string'
                  ? e.target.value.split(',')
                  : e.target.value;

              if (newCategories.includes('SELECT_ALL')) {
                const allCategoryNames = categories.map(
                  (cat) => cat.CategoryName
                );
                const isAllSelected =
                  formData.categories.length === categories.length;
                const finalCategories = isAllSelected ? [] : allCategoryNames;
                setFormData({ ...formData, categories: finalCategories });

                if (errors.categories && finalCategories.length > 0) {
                  setErrors((prev) => ({ ...prev, categories: '' }));
                }
              } else {
                setFormData({ ...formData, categories: newCategories });

                if (errors.categories && newCategories.length > 0) {
                  setErrors((prev) => ({ ...prev, categories: '' }));
                }
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 350,
                  borderRadius: 1.5,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  marginTop: 1
                }
              }
            }}
            input={<OutlinedInput label="Categories" />}
            renderValue={(selected) => selected.join(', ')}
            required
          >
            {/* ✅ Select All Option */}
            <MenuItem value="SELECT_ALL">
              <Checkbox
                checked={formData.categories.length === categories.length}
                indeterminate={
                  formData.categories.length > 0 &&
                  formData.categories.length < categories.length
                }
              />
              <ListItemText
                primary="Select All"
                sx={{ fontWeight: 'bold', color: 'primary.main' }}
              />
            </MenuItem>

            {/* ✅ Individual Categories */}
            {categories.map((category) => (
              <MenuItem key={category.CategoryID} value={category.CategoryName}>
                <Checkbox
                  checked={
                    formData.categories.indexOf(category.CategoryName) > -1
                  }
                />
                <ListItemText primary={category.CategoryName} />
              </MenuItem>
            ))}

            {/* ✅ Footer Button to Close Dropdown */}
            <MenuItem
              disableRipple
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                borderTop: '1px solid #ddd',
                mt: 1,
                pt: 1
              }}
              onClick={(e) => e.stopPropagation()} // ✅ Prevent MenuItem click
            >
              <button
                style={{
                  backgroundColor: '#1976d2',
                  color: 'white',
                  padding: '5px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
                onClick={(e) => {
                  e.stopPropagation(); // ✅ Prevent event bubbling
                  setOpenCategorySelect(false);
                }}
              >
                Apply
              </button>
            </MenuItem>
          </Select>
          {errors.categories && (
            <FormHelperText>{errors.categories}</FormHelperText>
          )}
        </FormControl>

        <Button
          color="primary"
          sx={{
            cursor: 'pointer',
            fontSize: '12px',
            textDecoration: 'underline',
            fontWeight: '700',
            '&:hover': {
              backgroundColor: 'transparent',
              boxShadow: 'none'
            }
          }}
          onClick={() => setIsCategoryModalOpen(true)}
        >
          + Create New
        </Button>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!errors.department} required>
          <InputLabel>Department</InputLabel>
          <Select
            value={formData.department}
            onChange={(e: SelectChangeEvent) => {
              setFormData({ ...formData, department: e.target.value });
              if (errors.department && e.target.value) {
                setErrors((prev) => ({ ...prev, department: '' }));
              }
            }}
            label="Department"
            required
          >
            {departments.map((dept) => (
              <MenuItem key={dept.DepartmentID} value={dept.DepartmentName}>
                {dept.DepartmentName}
              </MenuItem>
            ))}
          </Select>
          {errors.department && (
            <FormHelperText>{errors.department}</FormHelperText>
          )}

          {/* <Typography
            color="primary"
            sx={{
              cursor: 'pointer',
              textAlign: 'right',
              marginTop: '1px',
              fontSize: '12px',
              textDecoration: 'underline',
              fontWeight: '700'
            }}
            onClick={() => setIsDepartmentModalOpen(true)}
          >
            + Create New
          </Typography> */}
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={formData.status ? formData.status.toString() : ''}
            label="Status"
            onChange={(e: SelectChangeEvent) => {
              setFormData({ ...formData, status: e.target.value });
              if (errors.status && e.target.value) {
                setErrors((prev) => ({ ...prev, status: '' }));
              }
            }}
          >
            {statuses.map((item) => (
              <MenuItem key={item.StatusID} value={item.Status}>
                {item.Status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {formData.status === 'Awarded' && (
        <>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.awardedToVendor}>
              <InputLabel>Awarded To (Vendor)</InputLabel>
              <Select
                value={formData.awardedToVendor || ''}
                onChange={handleVendorChange}
                label="Awarded To (Vendor)"
                // disabled={loadingVendors || isDisabled}
                startAdornment={
                  loadingVendors ? (
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  ) : null
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 350,
                      borderRadius: 1.5,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                      marginTop: 1
                    }
                  }
                }}
              >
                {formData.awardedToVendor &&
                  !vendors.some(
                    (v) => v.name === formData.awardedToVendor
                  ) && (
                    <MenuItem value={formData.awardedToVendor}>
                      {formData.awardedToVendor} (Previously Selected)
                    </MenuItem>
                  )}
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.id} value={vendor.name}>
                    {vendor.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.awardedToVendor && (
                <FormHelperText>{errors.awardedToVendor}</FormHelperText>
              )}
              {vendorError && (
                <FormHelperText error>{vendorError}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.awardedToContact}>
              <InputLabel>Awarded To (Contact)*</InputLabel>
              <Select
                value={formData.awardedToContact || ''}
                onChange={handleContactChange}
                label="Awarded To (Contact)*"
                // fix this
                // disabled={
                //   !formData.awardedToVendor || loadingContacts || isDisabled
                // }
                startAdornment={
                  loadingContacts ? (
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  ) : null
                }
                required
              >
                {formData.awardedToContact &&
                  !contacts.some(
                    (c) => c.name === formData.awardedToContact
                  ) && (
                    <MenuItem value={formData.awardedToContact}>
                      {formData.awardedToContact} (Previously Selected)
                    </MenuItem>
                  )}
                {contacts.map((contact) => (
                  <MenuItem key={contact.id} value={contact.name}>
                    {contact.displayName}
                  </MenuItem>
                ))}
                {contacts.length === 0 &&
                  !loadingContacts &&
                  formData.awardedToVendor && (
                    <MenuItem disabled value="">
                      No contacts found for this vendor
                    </MenuItem>
                  )}
              </Select>
              {errors.awardedToContact && (
                <FormHelperText>{errors.awardedToContact}</FormHelperText>
              )}
              {contactError && (
                <FormHelperText error>{contactError}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </>
      )}

      {formData.status === 'Awarded' && (
        <>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Award Amount"
              type="text"
              value={formData.awardAmount ? addCommaWithTwoDecimals(formData.awardAmount) : ''}
              onChange={(e) => {
                const input = e.target.value.replace(/,/g, '');

                // allow only digits & dot
                if (!/^\d*\.?\d*$/.test(input)) return;
                setFormData({
                  ...formData,
                  awardAmount: input, // 🔥 store RAW value
                });
              }}
              onKeyDown={(e) => {
                if (['e', 'E', '+', '-'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </Grid>
        </>
      )}

      <Grid item xs={12} sm={formData.status === 'Awarded' ? 6 : 4}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Ad Date"
            value={formData.adDate}
            onChange={(newValue) => {
              setFormData({ ...formData, adDate: newValue });
            }}
            minDate={today}
            shouldDisableDate={(date) => {
              return date < today;
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true
                // helperText: getGoLiveDateError() || undefined,
                // error: !!getGoLiveDateError()
              }
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} sm={formData.status === 'Awarded' ? 6 : 4}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            label="Opening Date"
            value={formData.openingDate}
            onChange={(newValue) => {
              setFormData({ ...formData, openingDate: newValue });
              if (errors.openingDate) {
                setErrors((prev) => ({ ...prev, openingDate: '' }));
              }
            }}
            minDateTime={currentDateTime}
            shouldDisableDate={(date) => {
              return date < currentDateTime;
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!errors?.openingDate,
                helperText: errors?.openingDate
              }
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} sm={formData.status === 'Awarded' ? 6 : 4}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Effective Date"
            value={formData.effectiveDate}
            onChange={(newValue) => {
              setFormData({ ...formData, effectiveDate: newValue });
            }}
            shouldDisableDate={(date) => {
              return date < tomorrowDateTime;
            }}
            slotProps={{
              textField: {
                fullWidth: true
                // helperText: getAwardDateError() || undefined,
                // error: !!getAwardDateError()
              }
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={6}
          label="Description"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Admin Notes"
          value={formData.adminNotes}
          onChange={(e) =>
            setFormData({ ...formData, adminNotes: e.target.value })
          }
        />
      </Grid>

      <CreateTypeModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreate={handleCreateType}
      />
      <CreateCategoryModal
        open={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        onCreate={handleCreateCategory}
      />
      <CreateDepartmentModal
        open={isDepartmentModalOpen}
        onClose={() => setIsDepartmentModalOpen(false)}
        onCreate={handleCreateDepartment}
      />
    </Grid>
  );
};

export default BidDetailsForm;
